import { sendAppEvent } from "./nativeAnalyticsService";

export enum MedScreenName {
  CareTab = "care_tab",
  CareTabMarketplace = "care_tab_marketplace",
  YourPrescription = "your_prescription",
}

export enum MedButtonName {
  MedTreatmentStatusCtaTapped = "med_treatment_status_cta_tapped",
  CareTreatmentCardTapped = "care_treatment_card_tapped",
  PrescriptionWebviewCtaTapped = "prescription_webview_cta_tapped",
}

export enum MedCtaAction {
  FinishIntake = "finish_intake",
  Chat = "chat",
  TrackShipment = "track_shipment",
  MedHistory = "med_history",
  LogMedication = "log_medication",
  ViewResults = "view_results",
}

/** Stable `cardType` values for {@link MedCardDisplayedProto}. */
export enum MedCardType {
  TreatmentCard = "TreatmentCard",
}

export type MedCtaTappedParams = {
  ctaText: string;
  context: string;
  medicationName: string;
  ctaAction?: string;
};

/**
 * Shared dispatcher for every `medPageButtonClicked` event. Accepts a
 * partial payload because the proto only requires `buttonName` and
 * `screenName`; individual public wrappers below enforce stricter
 * contracts via their own argument types.
 */
function trackMedPageButtonClicked(
  buttonName: MedButtonName,
  screenName: MedScreenName,
  params: Partial<MedCtaTappedParams> = {}
): Promise<boolean> {
  return sendAppEvent({
    medPageButtonClicked: {
      buttonName,
      screenName,
      ...(params.context ? { context: params.context } : {}),
      ...(params.ctaText ? { ctaText: params.ctaText } : {}),
      ...(params.medicationName ? { medicationName: params.medicationName } : {}),
      ...(params.ctaAction ? { ctaAction: params.ctaAction } : {}),
    },
  });
}

/**
 * Shared dispatcher for every `medPageViewed` event. Same pattern as
 * `trackMedPageButtonClicked` — keeps the payload shape in one place.
 */
function trackMedPageViewed(
  screenName: MedScreenName,
  params: { medicationName?: string } = {}
): Promise<boolean> {
  return sendAppEvent({
    medPageViewed: {
      screenName,
      ...(params.medicationName
        ? { medicationName: params.medicationName }
        : {}),
    },
  });
}

export function trackMedTreatmentStatusCtaTapped(
  params: MedCtaTappedParams
): Promise<boolean> {
  return trackMedPageButtonClicked(
    MedButtonName.MedTreatmentStatusCtaTapped,
    MedScreenName.CareTab,
    params
  );
}

export function trackPrescriptionWebviewCtaTapped(
  params: MedCtaTappedParams
): Promise<boolean> {
  return trackMedPageButtonClicked(
    MedButtonName.PrescriptionWebviewCtaTapped,
    MedScreenName.YourPrescription,
    params
  );
}

export function trackCareTreatmentCardTapped(
  medicationName?: string
): Promise<boolean> {
  return trackMedPageButtonClicked(
    MedButtonName.CareTreatmentCardTapped,
    MedScreenName.CareTab,
    { medicationName }
  );
}

export type MedCardDisplayedParams = {
  treatmentName?: string;
  /**
   * Stable status identifier shown on the card, e.g. `"Shipped"`,
   * `"ShippingSoon"`. Prefer GraphQL status short names over localized copy.
   */
  cardState?: string;
};

/**
 * Fires `medCardDisplayed` for a Care-tab treatment card impression.
 * Call once per card when it is first rendered to the user.
 */
export function trackCareTreatmentCardDisplayed(
  params: MedCardDisplayedParams = {}
): Promise<boolean> {
  return sendAppEvent({
    medCardDisplayed: {
      cardType: MedCardType.TreatmentCard,
      screenName: MedScreenName.CareTab,
      ...(params.treatmentName ? { treatmentName: params.treatmentName } : {}),
      ...(params.cardState ? { cardState: params.cardState } : {}),
    },
  });
}

export function trackCareTabViewed(): Promise<boolean> {
  return trackMedPageViewed(MedScreenName.CareTab);
}

export function trackMarketplaceViewed(): Promise<boolean> {
  return trackMedPageViewed(MedScreenName.CareTabMarketplace);
}

export function trackYourPrescriptionWebviewViewed(
  medicationName?: string
): Promise<boolean> {
  return trackMedPageViewed(MedScreenName.YourPrescription, { medicationName });
}
